import * as React from 'react';

import { cn } from '@/utils';

import { useFullscreen } from './use-fullscreen';
import { useHLS } from './use-hls';
import { useVideoProgress } from './use-video-progress';
import { VideoControls } from './video-controls';
import { VideoOverlays } from './video-overlays';
import type { VideoChapter } from './video-seekbar';

export interface VideoPlayerProps
  extends Omit<React.VideoHTMLAttributes<HTMLVideoElement>, 'onTimeUpdate'> {
  src: string;
  poster?: string;
  className?: string;
  jumpTo?: number;
  chapters?: VideoChapter[];
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
}

const VideoPlayer = React.forwardRef<HTMLVideoElement, VideoPlayerProps>(
  (
    {
      src,
      poster,
      className,
      jumpTo,
      chapters = [],
      onPlay,
      onPause,
      onEnded,
      onTimeUpdate,
      ...props
    },
    ref
  ) => {
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [duration, setDuration] = React.useState(0);
    const [volume, setVolume] = React.useState(1);
    const [isMuted, setIsMuted] = React.useState(false);
    const [showControls, setShowControls] = React.useState(true);
    const [isBuffering, setIsBuffering] = React.useState(false);
    const [buffered, setBuffered] = React.useState(0);
    const [playbackRate, setPlaybackRate] = React.useState(1);
    const [showSpeedMenu, setShowSpeedMenu] = React.useState(false);

    const videoRef = React.useRef<HTMLVideoElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const progressBarRef = React.useRef<HTMLButtonElement>(null);
    const controlsTimeoutRef = React.useRef<ReturnType<
      typeof setTimeout
    > | null>(null);
    // Keep the auto-hide timer from closing controls while the speed menu is open.
    const speedMenuOpenRef = React.useRef(false);

    // Combine refs
    React.useImperativeHandle(ref, () => videoRef.current!);

    // Custom hooks
    const { isError, retry } = useHLS(videoRef, src);
    const { isFullscreen, toggleFullscreen } = useFullscreen(
      videoRef,
      containerRef
    );
    const {
      currentTime,
      handleTimeUpdate,
      handleProgressClick,
      handleMouseDown,
    } = useVideoProgress({
      videoRef,
      progressBarRef,
      duration,
      isPlaying,
      onTimeUpdate,
    });

    const handlePlayPause = () => {
      if (!videoRef.current) return;
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        void videoRef.current.play();
      }
    };

    const handleLoadedMetadata = () => {
      if (!videoRef.current) return;
      setDuration(videoRef.current.duration);
      // A media reload (src change / retry) resets playbackRate to 1.
      videoRef.current.playbackRate = playbackRate;
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newVolume = Number.parseFloat(e.target.value);
      setVolume(newVolume);
      if (videoRef.current) {
        videoRef.current.volume = newVolume;
      }
      setIsMuted(newVolume === 0);
    };

    const toggleMute = () => {
      if (!videoRef.current) return;
      if (isMuted) {
        videoRef.current.volume = volume;
        setIsMuted(false);
      } else {
        videoRef.current.volume = 0;
        setIsMuted(true);
      }
    };

    const skip = (delta: number) => {
      const video = videoRef.current;
      if (!video) return;
      const dur = duration || video.duration || 0;
      const target = video.currentTime + delta;
      video.currentTime = Math.max(0, dur ? Math.min(target, dur) : target);
    };

    // Track how far the media has buffered ahead of the playhead, for the
    // seekbar's loaded-progress indicator. Uses the buffered range that
    // contains the current position (there can be several after seeks).
    const updateBuffered = () => {
      const video = videoRef.current;
      if (!video) return;
      const { buffered: ranges, currentTime: time } = video;
      let end = time;
      for (let i = 0; i < ranges.length; i += 1) {
        if (ranges.start(i) <= time + 0.1 && time <= ranges.end(i)) {
          end = ranges.end(i);
          break;
        }
      }
      setBuffered(end);
    };

    const setSpeedMenu = (open: boolean) => {
      speedMenuOpenRef.current = open;
      setShowSpeedMenu(open);
    };

    const handleRateChange = (rate: number) => {
      setPlaybackRate(rate);
      if (videoRef.current) {
        videoRef.current.playbackRate = rate;
      }
      setSpeedMenu(false);
    };

    const showControlsTemporarily = () => {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        if (isPlaying && !speedMenuOpenRef.current) {
          setShowControls(false);
        }
      }, 3000);
    };

    // Keep the <video> element's playbackRate in sync with state.
    React.useEffect(() => {
      if (videoRef.current) {
        videoRef.current.playbackRate = playbackRate;
      }
    }, [playbackRate]);

    // Handle jumpTo prop
    React.useEffect(() => {
      if (jumpTo !== undefined && videoRef.current && duration > 0) {
        const timeToJump = Math.max(0, Math.min(jumpTo, duration));
        videoRef.current.currentTime = timeToJump;
      }
    }, [jumpTo, duration]);

    // Cleanup timeout on unmount
    React.useEffect(() => {
      return () => {
        const timeoutId = controlsTimeoutRef.current;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }, []);

    return (
      <div
        ref={containerRef}
        className={cn(
          'relative bg-black rounded-lg overflow-hidden group',
          isFullscreen && 'rounded-none',
          className
        )}
        onMouseMove={showControlsTemporarily}
        onMouseLeave={() => {
          setSpeedMenu(false);
          if (isPlaying) setShowControls(false);
        }}
      >
        <video
          ref={videoRef}
          poster={poster}
          className="w-full h-full object-contain"
          crossOrigin="use-credentials"
          playsInline
          preload="metadata"
          onPlay={() => {
            setIsPlaying(true);
            onPlay?.();
          }}
          onPause={() => {
            setIsPlaying(false);
            setIsBuffering(false);
            setShowControls(true);
            onPause?.();
          }}
          onEnded={() => {
            setIsPlaying(false);
            setIsBuffering(false);
            setShowControls(true);
            onEnded?.();
          }}
          // `waiting` = playback halted for data; clear on `playing`/`canplay`
          // and on `timeupdate` (progress proves we're not stalled). We do NOT
          // key off `stalled`: browsers (esp. Safari) fire it when they pause
          // fetching a *full* buffer, which would pin the spinner during
          // healthy playback.
          onWaiting={() => setIsBuffering(true)}
          onPlaying={() => setIsBuffering(false)}
          onCanPlay={() => setIsBuffering(false)}
          onProgress={updateBuffered}
          onTimeUpdate={() => {
            setIsBuffering(false);
            updateBuffered();
            handleTimeUpdate();
          }}
          onLoadedMetadata={handleLoadedMetadata}
          {...props}
        >
          <track kind="captions" />
        </video>

        <VideoOverlays
          isError={isError}
          isBuffering={isBuffering}
          isPlaying={isPlaying}
          showControls={showControls}
          onRetry={retry}
          onPlayPause={handlePlayPause}
        />

        <VideoControls
          currentTime={currentTime}
          duration={duration}
          buffered={buffered}
          chapters={chapters}
          isPlaying={isPlaying}
          isMuted={isMuted}
          volume={volume}
          playbackRate={playbackRate}
          showSpeedMenu={showSpeedMenu}
          isFullscreen={isFullscreen}
          showControls={showControls}
          progressBarRef={progressBarRef}
          onProgressClick={handleProgressClick}
          onProgressMouseDown={handleMouseDown}
          onPlayPause={handlePlayPause}
          onSkip={skip}
          onToggleMute={toggleMute}
          onVolumeChange={handleVolumeChange}
          onRateChange={handleRateChange}
          onToggleSpeedMenu={() => setSpeedMenu(!showSpeedMenu)}
          onToggleFullscreen={toggleFullscreen}
        />

        <style>{`
          .slider::-webkit-slider-thumb {
            appearance: none;
            height: 12px;
            width: 12px;
            border-radius: 50%;
            background: #3b82f6;
            cursor: pointer;
          }
          .slider::-moz-range-thumb {
            height: 12px;
            width: 12px;
            border-radius: 50%;
            background: #3b82f6;
            cursor: pointer;
            border: none;
          }
        `}</style>
      </div>
    );
  }
);

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;
